import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDollarSign, FiShoppingBag, FiTrendingUp, FiCalendar, FiRefreshCw, FiDownload } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FaRupeeSign } from 'react-icons/fa';


const RestaurantSales = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [salesData, setSalesData] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('month');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchRestaurantDetails();
  }, [restaurantId]);

  useEffect(() => {
    fetchSalesData();
  }, [restaurantId, filterType, dateRange]);

  const fetchRestaurantDetails = async () => {
    try {
      const response = await api.get(`/vendor-owner/restaurant/${restaurantId}`);
      if (response.data.success) {
        setRestaurant(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch restaurant details');
    }
  };

  const fetchSalesData = async () => {
    try {
      const params = { filterType };
      if (filterType === 'custom' && dateRange.startDate && dateRange.endDate) {
        params.startDate = dateRange.startDate;
        params.endDate = dateRange.endDate;
      }

      const response = await api.get(`/vendor-owner/restaurant/${restaurantId}/sales`, { params });
      if (response.data.success) {
        setSalesData(response.data.data);
        toast.success('Sales data refreshed successfully!');
      }
    } catch (error) {
      toast.error('Failed to fetch sales data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (type) => {
    setFilterType(type);
    if (type !== 'custom') {
      setDateRange({ startDate: '', endDate: '' });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading sales data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button 
              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
              onClick={() => navigate('/vendor-owner/restaurants')}
            >
              <FiArrowLeft size={16} />
              Back
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                {restaurant?.restaurantDetails?.restaurantName} - Sales Report
              </h1>
              <p className="text-gray-600">Detailed sales analytics and performance metrics</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button 
              className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
              onClick={fetchSalesData}
            >
              <FiRefreshCw size={16} />
              Refresh
            </button>
            <button 
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
              onClick={() => {
                // Export functionality can be added here
                toast.info('Export functionality coming soon!');
              }}
            >
              <FiDownload size={16} />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex items-center gap-2">
            <FiCalendar className="text-gray-600" size={20} />
            <span className="font-medium text-gray-700">Date Range:</span>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                filterType === 'today' 
                  ? 'bg-indigo-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => handleFilterChange('today')}
            >
              Today
            </button>
            <button
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                filterType === 'week' 
                  ? 'bg-indigo-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => handleFilterChange('week')}
            >
              This Week
            </button>
            <button
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                filterType === 'month' 
                  ? 'bg-indigo-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => handleFilterChange('month')}
            >
              This Month
            </button>
            <button
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 ${
                filterType === 'custom' 
                  ? 'bg-indigo-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => handleFilterChange('custom')}
            >
              <FiCalendar size={16} />
              Custom Range
            </button>
          </div>

          {filterType === 'custom' && (
            <div className="flex items-center gap-2 mt-4 lg:mt-0">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {salesData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <FaRupeeSign size={24} />
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-1">{formatCurrency(salesData.summary.totalRevenue || 0)}</div>
                <div className="text-green-100 text-sm">Total Revenue</div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <FiShoppingBag size={24} />
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-1">{salesData.summary.totalOrders || 0}</div>
                <div className="text-blue-100 text-sm">Total Orders</div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <FiTrendingUp size={24} />
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-1">{salesData.summary.completedOrders || 0}</div>
                <div className="text-purple-100 text-sm">Completed Orders</div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <FaRupeeSign size={24} />
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-1">{formatCurrency(salesData.summary.averageOrderValue || 0)}</div>
                <div className="text-orange-100 text-sm">Avg Order Value</div>
              </div>
            </div>
          </div>

          {/* Orders Table */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Recent Orders</h2>
              <p className="text-gray-600 text-sm">Latest orders for the selected period</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Order ID</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Customer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Amount</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {salesData.orders.map((order) => (
                    <tr key={order._id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className="font-mono text-sm font-medium text-indigo-600">#{order._id.slice(-8)}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900">{order.customerId?.fullName || 'Guest'}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{new Date(order.createdAt).toLocaleDateString()}</td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{formatCurrency(order.pricing?.total || order.totalAmount || 0)}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          order.status === 'completed' ? 'bg-green-100 text-green-800' :
                          order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          order.status === 'accepted' ? 'bg-blue-100 text-blue-800' :
                          order.status === 'preparing' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {salesData.orders.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiShoppingBag className="text-gray-400 text-2xl" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
                <p className="text-gray-600">No orders found for this period</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RestaurantSales;
